class TopBar extends HTMLElement {
  constructor() {
    super();
    this.expandBtn = this.querySelector('.top-bar__expand-btn');
    this.detailPanel = this.querySelector('.top-bar__detail');
    this.announcements = Array.from(this.querySelectorAll('.top-bar__announcement'));
    this.detailCards = Array.from(this.querySelectorAll('.top-bar__detail-card'));
    this.currentIndex = 0;
    this.autoplayTimer = null;
    this.isTransitioning = false;
    this.hoverExpand = this.hasAttribute('data-hover-expand');

    this.bindEvents();

    if (this.announcements.length > 1 && this.dataset.autoplay === 'true') {
      this.startAutoplay();
    }
  }

  bindEvents() {
    this._megaCloseHandler = () => {
      document.dispatchEvent(new CustomEvent('mega-menu:close'));
    };
    this.expandBtn?.addEventListener('mouseenter', this._megaCloseHandler);

    if (this.hoverExpand && this.expandBtn && this.detailPanel) {
      const isTouch = window.matchMedia('(hover: none)').matches;

      if (!isTouch) {
        let enterTimeout;

        this.expandBtn.addEventListener('mouseenter', () => {
          enterTimeout = setTimeout(() => this.openDetail(), 120);
        });

        this.expandBtn.addEventListener('mouseleave', () => {
          clearTimeout(enterTimeout);
        });

        // Desktop hover: click always opens (mouseleave handles close).
        // Using toggle here caused a race condition on Safari where the
        // 120ms hover timer opened the panel, then the click immediately
        // closed it.
        this.expandBtn.addEventListener('click', () => this.openDetail());

        // Close when mouse leaves the entire <top-bar> element
        this.addEventListener('mouseleave', () => {
          this.closeDetail();
        });
      } else {
        // Touch: click toggles, tap-outside closes
        this.expandBtn.addEventListener('click', () => this.toggleDetail());
      }

      this._onDocumentClick = (e) => {
        if (!this.contains(e.target)) {
          this.closeDetail();
        }
      };
    } else {
      // No hover-expand: simple click toggle
      this.expandBtn?.addEventListener('click', () => this.toggleDetail());
    }
  }

  openDetail() {
    if (!this.expandBtn || !this.detailPanel) return;
    this.expandBtn.setAttribute('aria-expanded', 'true');
    this.detailPanel.setAttribute('aria-hidden', 'false');
    this.stopAutoplay();
    if (this._onDocumentClick) {
      document.addEventListener('click', this._onDocumentClick);
    }
  }

  closeDetail() {
    if (!this.expandBtn || !this.detailPanel) return;
    this.expandBtn.setAttribute('aria-expanded', 'false');
    this.detailPanel.setAttribute('aria-hidden', 'true');
    if (this._onDocumentClick) {
      document.removeEventListener('click', this._onDocumentClick);
    }
    if (this.announcements.length > 1 && this.dataset.autoplay === 'true') {
      this.startAutoplay();
    }
  }

  toggleDetail() {
    const isExpanded = this.expandBtn.getAttribute('aria-expanded') === 'true';
    if (isExpanded) {
      this.closeDetail();
    } else {
      this.openDetail();
    }
  }

  startAutoplay() {
    const speed = (parseInt(this.dataset.speed, 10) || 5) * 1000;
    this.stopAutoplay();
    this.autoplayTimer = setInterval(() => this.nextAnnouncement(), speed);
  }

  disconnectedCallback() {
    this.stopAutoplay();
    if (this._megaCloseHandler) {
      this.expandBtn?.removeEventListener('mouseenter', this._megaCloseHandler);
    }
    if (this._onDocumentClick) {
      document.removeEventListener('click', this._onDocumentClick);
    }
  }

  stopAutoplay() {
    if (this.autoplayTimer) {
      clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }
  }

  nextAnnouncement() {
    if (this.isTransitioning) return;
    this.isTransitioning = true;

    const current = this.announcements[this.currentIndex];

    current.classList.add('top-bar__announcement--fading');

    setTimeout(() => {
      current.classList.remove('top-bar__announcement--fading');
      current.classList.add('top-bar__announcement--hidden');

      this.currentIndex = (this.currentIndex + 1) % this.announcements.length;

      const next = this.announcements[this.currentIndex];

      // Move the active card to the front of the grid via CSS order
      this.detailCards.forEach((card, i) => {
        card.style.order = i === this.currentIndex ? '-1' : '';
      });

      next.classList.remove('top-bar__announcement--hidden');
      next.classList.add('top-bar__announcement--entering');
      setTimeout(() => {
        next.classList.remove('top-bar__announcement--entering');
        this.isTransitioning = false;
      }, 200);
    }, 200);
  }
}

customElements.define('top-bar', TopBar);
